import { Link } from "react-router-dom";
import { SUPPORT_EMAIL } from "@/components/PublicLayout";

/**
 * /messaging — Affirmative-consent compliance page for Twilio / carrier
 * toll-free verification (10DLC / TFN). Rewritten for V9.14 after a
 * 30513 rejection to make every consent requirement carrier-reviewers
 * look for visually obvious and unambiguous.
 */

/* ── Example SMS bodies (illustrative, 555-prefix) ─────────────── */

const REMINDER_EXAMPLES = [
  {
    from: "+1 (415) 555-0142",
    body:
      "Journey reminder: Call the dentist at 3:00 PM today. Reply HELP for help, STOP to opt out.",
  },
  {
    from: "+1 (415) 555-0142",
    body:
      "Journey here — heads up that your lease renewal window opens in 7 days. Reply HELP for help, STOP to opt out.",
  },
];

const TASK_EXAMPLES = [
  {
    role: "Journey → recipient",
    from: "+1 (415) 555-0142",
    body:
      "Hi Maya — Monte asked me to check in: are you free Friday afternoon to review the Q1 plan? Reply HELP for help, STOP to opt out.",
  },
  {
    role: "Recipient → Journey",
    from: "+1 (628) 555-0193",
    body: "Working on it — should have a draft by Thursday.",
  },
  {
    role: "Journey → recipient",
    from: "+1 (415) 555-0142",
    body:
      "Got it, I'll let Monte know. Reply HELP for help, STOP to opt out.",
  },
];

/* ── Building-block components ─────────────────────────────────── */

const Pill = ({ children }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/25 text-cyan-200 text-[11px] uppercase tracking-wider">
    {children}
  </span>
);

const SmsBubble = ({ from, body, role }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] text-white/45 font-mono">{from}</span>
      {role ? (
        <span className="text-[11px] text-cyan-300/80 uppercase tracking-wider">
          {role}
        </span>
      ) : null}
    </div>
    <p className="text-[15px] leading-relaxed text-white/85">{body}</p>
  </div>
);

const Section = ({ id, eyebrow, title, children }) => (
  <section id={id} className="mb-14 scroll-mt-24" data-testid={`messaging-section-${id}`}>
    {eyebrow ? (
      <div className="text-xs uppercase tracking-wider text-cyan-300/80 mb-2">
        {eyebrow}
      </div>
    ) : null}
    <h2 className="font-display text-xl sm:text-2xl mb-4 text-white">{title}</h2>
    <div className="text-white/70 leading-relaxed space-y-4 text-[15px]">{children}</div>
  </section>
);

/* ── Mock consent UI (screenshot-ready for carrier review) ─────── */

const ConsentMockup = () => (
  <div
    className="rounded-3xl border border-white/12 bg-[#0a0a0a] overflow-hidden shadow-2xl shadow-cyan-500/5"
    data-testid="messaging-consent-mockup"
  >
    {/* Mock browser/app chrome */}
    <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/8 bg-white/[0.02]">
      <span className="w-2.5 h-2.5 rounded-full bg-rose-400/60" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
      <div className="flex-1 text-center">
        <span className="text-[11px] text-white/45 font-mono">
          journey · enable SMS
        </span>
      </div>
    </div>

    {/* Modal body */}
    <div className="p-6 sm:p-8">
      <div className="text-xs uppercase tracking-wider text-cyan-300/80 mb-3">
        Enable SMS reminders
      </div>
      <p className="text-[14px] text-white/65 mb-5 leading-relaxed">
        Journey can send your reminders and delegated task follow-ups by SMS.
        Messaging is off by default. Confirm your phone number and opt in to
        turn it on.
      </p>

      <label className="block text-[11px] uppercase tracking-wider text-white/45 mb-1.5">
        Your mobile number
      </label>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-[15px] font-mono text-white/85 mb-5">
        +1 (415) 555-0142
      </div>

      {/* THE consent checkbox — exact wording carriers look for */}
      <label
        htmlFor="consent-mockup"
        className="flex items-start gap-3 cursor-default rounded-xl border border-cyan-400/30 bg-cyan-500/[0.07] p-4 mb-4"
      >
        <span
          id="consent-mockup"
          aria-checked="true"
          role="checkbox"
          className="mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 border-cyan-300 bg-cyan-400 flex items-center justify-center"
          data-testid="messaging-consent-checkbox"
        >
          <svg viewBox="0 0 16 16" className="w-3 h-3 text-[#04141a]" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8l3.5 3.5L13 5" />
          </svg>
        </span>
        <span className="text-[13.5px] text-white/85 leading-relaxed">
          I agree to receive SMS messages from Journey at the number above
          for the reminders and delegated task follow-ups I create inside
          the app. Message frequency varies and depends on the reminders
          and tasks I set up. Message-and-data rates may apply. I can
          reply <strong className="text-cyan-200">STOP</strong> at any
          time to cancel, or <strong className="text-cyan-200">HELP</strong> for
          help. Consent is not a condition of using Journey.
        </span>
      </label>

      <button
        type="button"
        disabled
        className="w-full rounded-xl bg-cyan-400 text-[#04141a] py-3 text-sm font-medium opacity-80 cursor-default"
        data-testid="messaging-consent-cta"
      >
        Enable SMS
      </button>

      <p className="text-[11px] text-white/40 mt-3 text-center">
        Your number is used only for these messages. See our{" "}
        <Link to="/privacy" className="text-cyan-300/80 hover:text-cyan-200 underline-offset-2 hover:underline">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link to="/terms" className="text-cyan-300/80 hover:text-cyan-200 underline-offset-2 hover:underline">
          Terms
        </Link>
        .
      </p>
    </div>
  </div>
);

/* ── Three-step opt-in flow ────────────────────────────────────── */

const OptInStep = ({ n, title, body, testId }) => (
  <div
    className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 text-xs font-mono">
        {n}
      </span>
      <span className="text-[11px] uppercase tracking-wider text-cyan-300/80">
        Step {n}
      </span>
    </div>
    <div className="font-display text-base text-white mb-2">{title}</div>
    <p className="text-[14px] text-white/65 leading-relaxed">{body}</p>
  </div>
);

/* ── Page ──────────────────────────────────────────────────────── */

export default function Messaging() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Pill>Messaging compliance</Pill>
        <Pill>Affirmative opt-in</Pill>
        <Pill>10DLC / Toll-free</Pill>
      </div>
      <h1
        className="font-display text-3xl sm:text-4xl tracking-tight mb-3"
        data-testid="messaging-heading"
      >
        How Journey uses SMS
      </h1>
      <p className="text-white/60 text-base leading-relaxed mb-10 max-w-2xl">
        Journey is a single-owner AI assistant. SMS is{" "}
        <strong className="text-white/90">off by default</strong> and is
        only enabled by the account owner after they{" "}
        <strong className="text-white/90">voluntarily provide their phone
        number</strong> and{" "}
        <strong className="text-white/90">explicitly opt in</strong>{" "}
        inside the app. No SMS is sent without that affirmative consent.
        All messaging is transactional — no marketing, no bulk, no
        third-party lists.
      </p>

      {/* Affirmative consent contract — visually obvious for reviewers */}
      <div
        className="rounded-3xl border border-cyan-400/25 bg-cyan-500/[0.04] p-6 sm:p-7 mb-14"
        data-testid="messaging-affirmative-consent"
      >
        <div className="text-xs uppercase tracking-wider text-cyan-300/90 mb-3">
          Affirmative consent — our four guarantees
        </div>
        <ul className="space-y-3 text-[14.5px] text-white/85 leading-relaxed">
          <li className="flex gap-3">
            <span className="text-cyan-300 font-bold shrink-0">1.</span>
            <span>
              <strong className="text-white">Users voluntarily provide their phone number.</strong>{" "}
              The number is typed in by the account owner inside their own
              private app session. We do not collect numbers from
              third-party lists, scraping, lookups, or any external source.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-cyan-300 font-bold shrink-0">2.</span>
            <span>
              <strong className="text-white">Users explicitly opt in before receiving SMS.</strong>{" "}
              The owner must check an unticked consent box and tap{" "}
              <em>Enable SMS</em>. The checkbox text states clearly what
              they will receive, that message-and-data rates may apply, and
              that they can reply STOP at any time.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-cyan-300 font-bold shrink-0">3.</span>
            <span>
              <strong className="text-white">No SMS is ever sent without consent.</strong>{" "}
              Until the consent record exists in our database, the SMS
              sending path is disabled at the server level. Revoking
              consent (via STOP or via the in-app toggle) immediately
              disables it again.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-cyan-300 font-bold shrink-0">4.</span>
            <span>
              <strong className="text-white">Messaging is transactional only.</strong>{" "}
              SMS is used solely for (a) reminders the owner created for
              themselves, and (b) one-to-one task follow-ups the owner
              initiated with named contacts. There is no marketing, no
              bulk/broadcast messaging, no promotional content, no
              affiliate links, and no list-based sending of any kind.
            </span>
          </li>
        </ul>
      </div>

      <Section id="opt-in-flow" eyebrow="Affirmative consent" title="How users opt in">
        <p>
          Every Journey account owner goes through the same three-step
          consent flow before a single SMS leaves the server. The user
          initiates the flow themselves — Journey never pre-fills, pre-checks,
          or assumes any prior agreement.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mt-5">
          <OptInStep
            n="1"
            title="User enters their own number"
            body="From Settings → Messaging, the owner voluntarily types in the mobile number they want reminders sent to. Nothing is auto-discovered."
            testId="opt-in-step-1"
          />
          <OptInStep
            n="2"
            title="User checks the consent box"
            body="An unticked checkbox shows the full consent statement — what they'll receive, frequency disclosure, rates may apply, STOP/HELP. The user must check it themselves."
            testId="opt-in-step-2"
          />
          <OptInStep
            n="3"
            title="User taps Enable SMS"
            body="The server records the consent (timestamp, IP, number, exact wording) and only then permits SMS sending for that account. Tapping the in-app toggle off — or replying STOP — revokes it immediately."
            testId="opt-in-step-3"
          />
        </div>
      </Section>

      <Section id="consent-mockup" eyebrow="Sample opt-in screen" title="The consent UI users see">
        <p>
          Below is the exact opt-in screen presented to account owners
          inside the app. The consent text is verbatim what is shown — and
          what is stored alongside the user's affirmative checkbox tick.
        </p>
        <div className="mt-5">
          <ConsentMockup />
        </div>
        <div className="text-[12px] text-white/40 text-center mt-3">
          Screenshot-ready mockup · matches the in-app opt-in screen
        </div>
      </Section>

      <Section id="user-initiates" eyebrow="User-initiated" title="Users initiate every reminder and task themselves">
        <p>
          Journey never schedules a reminder or sends a task message on
          its own. The account owner explicitly creates each reminder
          (via chat, the Reminders page, or the API) and explicitly
          creates each delegated task by adding a contact and writing the
          message themselves.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-[14.5px]">
          <li>
            <strong className="text-white/90">Reminders</strong> — typed
            into chat ("remind me Friday at 4pm") or entered manually on
            the Reminders page. SMS delivery only occurs if the owner has
            completed the opt-in flow above.
          </li>
          <li>
            <strong className="text-white/90">Delegated tasks</strong> —
            the owner manually adds a contact (name + phone number they
            already have permission to message) and writes the follow-up
            content. Journey never auto-discovers contacts.
          </li>
          <li>
            <strong className="text-white/90">Inbound replies</strong> —
            handled only within an active task the owner created.
            Recipients who reply outside any active task receive no
            further messages.
          </li>
        </ul>
      </Section>

      <Section id="examples" eyebrow="Sample messages" title="Example SMS bodies">
        <p>
          Every outbound message includes Journey's identity, the originating
          context, and the standard STOP/HELP guidance. Phone numbers below
          are 555-prefix placeholders for illustration.
        </p>

        <div className="text-[13px] text-white/50 uppercase tracking-wider mt-6 mb-3">
          Personal reminders (owner → owner)
        </div>
        <div className="space-y-3">
          {REMINDER_EXAMPLES.map((m, i) => (
            <SmsBubble key={`r-${i}`} from={m.from} body={m.body} />
          ))}
        </div>

        <div className="text-[13px] text-white/50 uppercase tracking-wider mt-8 mb-3">
          Delegated task follow-up (full thread)
        </div>
        <div className="space-y-3">
          {TASK_EXAMPLES.map((m, i) => (
            <SmsBubble key={`t-${i}`} from={m.from} body={m.body} role={m.role} />
          ))}
        </div>
      </Section>

      <Section id="keywords" eyebrow="Opt-out" title="STOP / HELP — always honored">
        <p>
          Every outbound SMS Journey sends ends with the standard{" "}
          <strong className="text-white/90">"Reply HELP for help, STOP to opt out."</strong>{" "}
          tail. Recipient keywords are processed at the carrier level by
          Twilio and immediately at our server:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-[14.5px]">
          <li>
            Reply{" "}
            <code className="text-cyan-200">STOP</code>,{" "}
            <code className="text-cyan-200">STOPALL</code>,{" "}
            <code className="text-cyan-200">UNSUBSCRIBE</code>,{" "}
            <code className="text-cyan-200">CANCEL</code>,{" "}
            <code className="text-cyan-200">END</code>, or{" "}
            <code className="text-cyan-200">QUIT</code> — all further
            messages cease. The account owner is also notified so the
            contact is removed from any open task.
          </li>
          <li>
            Reply <code className="text-cyan-200">HELP</code> or{" "}
            <code className="text-cyan-200">INFO</code> — a short
            description of the service plus the support email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
              {SUPPORT_EMAIL}
            </a>{" "}
            is returned at no cost.
          </li>
          <li>
            Email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
              {SUPPORT_EMAIL}
            </a>{" "}
            at any time to request opt-out, deletion of records, or any
            other change. Requests are honored within a reasonable
            timeframe (typically under 30 days).
          </li>
        </ul>
        <p>
          Standard message-and-data rates from the recipient's carrier may
          apply. Message frequency varies and is bounded entirely by the
          reminders and tasks the account owner schedules — Journey does
          not send recurring campaigns.
        </p>
      </Section>

      <Section id="not-promotional" eyebrow="Scope" title="No marketing · No bulk · No third-party lists">
        <p>
          Journey is explicitly <strong className="text-white/90">not</strong>{" "}
          a marketing or broadcast platform. There is no campaign builder,
          no audience targeting, no promotional templates, no affiliate
          content, and no list-based sending. Phone numbers entered into
          Journey are never sold, shared, lent, or used for any purpose
          beyond the messaging the owner explicitly initiated.
        </p>
        <p>
          This is also enforced contractually — see{" "}
          <Link to="/terms" className="text-cyan-300 hover:text-cyan-200">
            /terms
          </Link>{" "}
          §SMS messaging terms and{" "}
          <Link to="/privacy" className="text-cyan-300 hover:text-cyan-200">
            /privacy
          </Link>{" "}
          §SMS messaging.
        </p>
      </Section>

      <Section id="contact" eyebrow="Contact" title="Questions or compliance requests">
        <p>
          For any messaging-related question — consent verification,
          opt-out, data removal, or carrier compliance inquiries — please
          email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          . Requests are answered by a human, typically within a few
          business days.
        </p>
      </Section>
    </div>
  );
}
